import { useEffect, useRef, useState } from 'react';
import type { Material } from '../type/Material';
import type { MaterialLot } from '../type/MaterialLot';
import type { TraceabilityApiClient } from '../api/TraceabilityApiClient';
import { useTranslation } from '@/hooks/useTranslation';

type FormLot = {
  supplier: string;
  lotNumber: string;
};

type SuccessDetails = {
  id: number;
  supplier: string;
  lotNumber: string;
};

type UseMaterialLotManagerReturn = {
  materials: Material[];
  activeMaterials: Material[];
  lots: MaterialLot[];
  selectedMaterialId: number | null;
  formLot: FormLot;
  isLoadingMaterials: boolean;
  isLoadingLots: boolean;
  isSaving: boolean;
  formError: string | null;
  successId: number | null;
  successDetails: SuccessDetails | null;
  setSelectedMaterialId: (id: number | null) => void;
  setFormLot: (lot: FormLot) => void;
  handleFindOrCreate: () => Promise<void>;
  handleReset: () => void;
};

export function useMaterialLotManager(
  traceabilityApiClient: TraceabilityApiClient | null
): UseMaterialLotManagerReturn {
  const { t } = useTranslation();

  const [materials, setMaterials] = useState<Material[]>([]);
  const [lots, setLots] = useState<MaterialLot[]>([]);
  const [selectedMaterialId, setSelectedMaterialIdState] = useState<number | null>(null);
  const [formLot, setFormLot] = useState<FormLot>({ supplier: '', lotNumber: '' });
  const [isLoadingMaterials, setIsLoadingMaterials] = useState(false);
  const [isLoadingLots, setIsLoadingLots] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successId, setSuccessId] = useState<number | null>(null);
  const [successDetails, setSuccessDetails] = useState<SuccessDetails | null>(null);

  const writeControllerRef = useRef<AbortController | null>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      writeControllerRef.current?.abort();
      if (successTimerRef.current !== null) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  // Fetch all materials on mount
  useEffect(() => {
    if (!traceabilityApiClient) return;
    const ac = new AbortController();
    setIsLoadingMaterials(true);
    setFormError(null);

    traceabilityApiClient
      .materialReadAll({}, { signal: ac.signal })
      .then((response) => {
        if (ac.signal.aborted) return;
        if (response.ok && response.data?.materials) {
          setMaterials(
            response.data.materials.map((item: { material: Material }) => item.material)
          );
        } else {
          if (!response.ok && response.code === 401) return;
          setFormError(
            response.ok
              ? t('traceability.materialLotManager.errors.loadFailed')
              : response.message || t('traceability.materialLotManager.errors.loadFailed')
          );
        }
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted) return;
        console.error('Failed to fetch materials for lot manager:', err);
        setFormError(t('traceability.materialLotManager.errors.loadFailed'));
      })
      .finally(() => {
        if (!ac.signal.aborted) setIsLoadingMaterials(false);
      });

    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [traceabilityApiClient]);

  // Fetch lots when selectedMaterialId changes
  useEffect(() => {
    if (!traceabilityApiClient || selectedMaterialId === null) {
      setLots([]);
      return;
    }

    const ac = new AbortController();
    setIsLoadingLots(true);
    setFormError(null);

    traceabilityApiClient
      .materialLotReadForMaterial({ materialId: selectedMaterialId }, { signal: ac.signal })
      .then((response) => {
        if (ac.signal.aborted) return;
        if (response.ok && response.data?.lots) {
          setLots(response.data.lots);
        } else {
          if (!response.ok && response.code === 401) return;
          setFormError(
            response.ok
              ? t('traceability.materialLotManager.errors.loadFailed')
              : response.message || t('traceability.materialLotManager.errors.loadFailed')
          );
        }
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted) return;
        console.error('Failed to fetch material lots:', err);
        setFormError(t('traceability.materialLotManager.errors.loadFailed'));
      })
      .finally(() => {
        if (!ac.signal.aborted) setIsLoadingLots(false);
      });

    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [traceabilityApiClient, selectedMaterialId]);

  const refreshLots = async (materialId: number, signal: AbortSignal) => {
    if (!traceabilityApiClient) return;
    setIsLoadingLots(true);
    try {
      const response = await traceabilityApiClient.materialLotReadForMaterial(
        { materialId },
        { signal }
      );
      if (signal.aborted) return;
      if (response.ok && response.data?.lots) {
        setLots(response.data.lots);
      }
    } catch (err: unknown) {
      console.error('Failed to refresh lots:', err);
    } finally {
      if (!signal.aborted) setIsLoadingLots(false);
    }
  };

  const setSelectedMaterialId = (id: number | null) => {
    setSelectedMaterialIdState(id);
    setFormError(null);
    setSuccessId(null);
    setSuccessDetails(null);
    if (successTimerRef.current !== null) {
      clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }
  };

  const handleFindOrCreate = async () => {
    if (!traceabilityApiClient || selectedMaterialId === null) {
      setFormError(t('traceability.materialLotManager.errors.saveFailed'));
      return;
    }

    writeControllerRef.current?.abort();
    const ac = new AbortController();
    writeControllerRef.current = ac;

    setIsSaving(true);
    setFormError(null);
    setSuccessId(null);

    try {
      const response = await traceabilityApiClient.materialLotFindOrCreate(
        {
          materialId: selectedMaterialId,
          supplier: formLot.supplier.trim() || null,
          lotNumber: formLot.lotNumber.trim() || null,
        },
        { signal: ac.signal }
      );

      if (ac.signal.aborted) return;

      if (response.ok) {
        const newId = response.data.id;
        // Capture displayed values before clearing the form
        const capturedSupplier = formLot.supplier.trim();
        const capturedLotNumber = formLot.lotNumber.trim();
        setSuccessId(newId);
        setSuccessDetails({ id: newId, supplier: capturedSupplier, lotNumber: capturedLotNumber });
        setFormLot({ supplier: '', lotNumber: '' });

        if (successTimerRef.current !== null) clearTimeout(successTimerRef.current);
        successTimerRef.current = setTimeout(() => {
          setSuccessId(null);
          setSuccessDetails(null);
          successTimerRef.current = null;
        }, 3000);

        await refreshLots(selectedMaterialId, ac.signal);
      } else {
        if (response.code === 401) return;
        setFormError(response.message || t('traceability.materialLotManager.errors.saveFailed'));
      }
    } catch (err: unknown) {
      if (ac.signal.aborted) return;
      console.error('Failed to find or create material lot:', err);
      setFormError(t('traceability.materialLotManager.errors.saveFailed'));
    } finally {
      if (!ac.signal.aborted) setIsSaving(false);
    }
  };

  const handleReset = () => {
    setFormLot({ supplier: '', lotNumber: '' });
    setFormError(null);
    setSuccessId(null);
    setSuccessDetails(null);
    if (successTimerRef.current !== null) {
      clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }
  };

  const activeMaterials = materials
    .filter((m) => !m.archived)
    .sort((a, b) => a.name.localeCompare(b.name));

  return {
    materials,
    activeMaterials,
    lots,
    selectedMaterialId,
    formLot,
    isLoadingMaterials,
    isLoadingLots,
    isSaving,
    formError,
    successId,
    successDetails,
    setSelectedMaterialId,
    setFormLot,
    handleFindOrCreate,
    handleReset,
  };
}
